import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Row, Col } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';
import DatePicker from 'react-datepicker';

import Modal from 'components/modal';
import { includes } from 'lodash';

export default function AllFiltersModal({
  closeModal,
  dataForFilters,
  dynamicFilters,
  show,
  staringFilters,
  onChangeFilters,
}) {
  const [filters, setFilters] = useState(staringFilters);

  useEffect(() => {
    resetFilters();
  }, [show]);

  const onStartDateUpdate = (date) => {
    setFilters({ ...filters, startDate: date });
  };

  const onEndDateUpdate = (date) => {
    setFilters({ ...filters, endDate: date });
  };

  const checkboxDisplay = (filter, option) => {
    return (
      <Form.Check
        aria-label={option.label}
        checked={includes(filters[filter], option.value)}
        id={`${filter}-${option.value}`}
        key={option.value}
        label={option.label}
        name={`${option.label}_check`}
        type="checkbox"
        onChange={e => changeFilters(e, filter, option.value)}
      />
    );
  };

  const renderFilterBox = (filterKey, dataKey, label, topFilter = true, spanFullLength = false) => {
    const options = dataForFilters[dataKey];

    return (
      <Col className="d-flex flex-column" sm={spanFullLength ? 12 : 6}>
        <p className={`fs-large fw-semibold mb-1 ${topFilter ? 'mt-4' : 'mt-0'}`}>{label}</p>
        <Card className="flex-grow-1 overflow-y-auto" style={{ maxHeight: '100px' }}>
          <Card.Body className="px-2 py-1">
            {options && options.map(option => (
              checkboxDisplay(filterKey, option)
            ))}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const saveFilters = () => {
    onChangeFilters(filters);
    closeModal();
  };

  const changeFilters = (e, filter, optionId) => {
    const { checked } = e.target;
    if (checked) {
      setFilters({ ...filters, [filter]: [...filters[filter], optionId] });
    } else {
      setFilters({ ...filters, [filter]: filters[filter].filter(id => id !== optionId) });
    }
  };

  const resetFilters = () => setFilters(staringFilters);

  const cancelFilters = () => {
    resetFilters();
    closeModal();
  };

  const clearFilters = () => {
    const cleared = Object.fromEntries(
      Object.keys(filters).map(key => [key, Array.isArray(filters[key]) ? [] : null])
    );
    setFilters(cleared);
  };

  const validDynamicFilters = dynamicFilters.filter((dynamicFilter) => {
    const options = dataForFilters[dynamicFilter.dataKey];
    return options.length > 1;
  });

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        All Filters
      </Modal.Header>

      <Modal.Body>
        <Row className="d-flex flex-wrap">
          {validDynamicFilters.map((dynamicFilter, index) => {
            const hasTopFilter = index > 1;
            const isLastFilterAndIsOdd = index === validDynamicFilters.length - 1 && (index + 1) % 2 === 1;
            return (
              <React.Fragment key={dynamicFilter.dataKey}>
                {renderFilterBox(dynamicFilter.filterKey, dynamicFilter.dataKey, dynamicFilter.label,
                  hasTopFilter, isLastFilterAndIsOdd)}
              </React.Fragment>
            );
          })}
        </Row>

        <p className="fs-large fw-semibold mb-1 mt-4">Date Added</p>
        <Row>
          <Col sm={6}>
            <DatePicker
              className="form-control"
              maxDate={filters.endDate}
              placeholderText="Start Date"
              selected={filters['startDate']}
              wrapperClassName="w-100"
              onChange={date => onStartDateUpdate(date)}
            />
          </Col>
          <Col sm={6}>
            <DatePicker
              className="form-control"
              minDate={filters.startDate}
              placeholderText="End Date"
              selected={filters['endDate']}
              wrapperClassName="w-100"
              onChange={date => onEndDateUpdate(date)}
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center mt-4">
          <Button label="X Clear All" name="clearFilters" variant="secondary" onClick={clearFilters} />
          <div>
            <Button className="me-2" label="Cancel" name="cancel" variant="ghost" onClick={cancelFilters} />
            <Button label="Apply Filters" name="saveFilters" onClick={saveFilters} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

AllFiltersModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  dataForFilters: PropTypes.object.isRequired,
  dynamicFilters: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string.isRequired,
    filterKey: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  show: PropTypes.bool.isRequired,
  staringFilters: PropTypes.object.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
};
